/**
 * Gear Detail Page
 * 
 * Displays full details for a single gear item including:
 * - Name, category, intensity
 * - Description, usage notes, safety notes
 * - Linked kinks and sex actions
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Package, Zap, Lock, Target, Shirt, AlertTriangle, Lightbulb, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { Spinner } from '../components/Animations';
import PageSections from '../components/PageSections';

// Intensity color based on level (1-10)
const getIntensityColor = (intensity) => {
    if (intensity <= 3) return 'text-emerald-400 bg-emerald-500/10';
    if (intensity <= 6) return 'text-amber-400 bg-amber-500/10';
    return 'text-rose-400 bg-rose-500/10';
};

const GearDetailPage = () => {
    const { id } = useParams();
    const [gear, setGear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchGear();
    }, [id]);

    const fetchGear = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getGearById(id);
            setGear(data);
        } catch (err) {
            console.error('Error fetching gear:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" className="mx-auto mb-4" />
                    <p className="text-dark-400">Loading gear...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !gear) {
        return (
            <div className="min-h-screen bg-charcoal flex items-center justify-center">
                <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-white mb-2">Gear Not Found</h2>
                    <p className="text-dark-400 mb-4">{error || 'This gear item does not exist.'}</p>
                    <Link to="/gear" className="btn-primary">
                        Back to Gear
                    </Link>
                </div>
            </div>
        );
    }

    const intensityClass = getIntensityColor(gear.intensity || 5);
    const showRelatedKinks = (gear.kinks?.length || 0) >= 4;
    const showRelatedActions = (gear.sexActions?.length || 0) >= 4;

    return (
        <div className="min-h-screen bg-charcoal">
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Back Navigation */}
                <Link
                    to="/gear"
                    className="inline-flex items-center gap-2 text-dark-400 hover:text-white transition-colors mb-6"
                >
                    <ArrowLeft size={20} />
                    <span>Back to Gear</span>
                </Link>

                {/* Parent Variant Breadcrumb */}
                {gear.parentVariant && (
                    <div className="flex items-center gap-2 text-sm text-dark-400 mb-4">
                        <span>Sub-variant of</span>
                        <Link
                            to={`/gear/${gear.parentVariant.id}`}
                            className="text-[#C4A35A] hover:text-[#C4A35A]/80 transition-colors font-medium flex items-center gap-1"
                        >
                            {gear.parentVariant.name}
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                )}

                {/* Header */}
                <div className="relative overflow-hidden rounded-2xl bg-[#0A0A0A]/80 border border-wine-800/30 p-8 mb-8">
                    {/* Ambient glow - fantasy */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#C4A35A]/8 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-wine-700/15 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10">
                        <div className="flex items-start gap-4 mb-4">
                            {/* Icon */}
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#C4A35A]/10 flex items-center justify-center text-[#C4A35A]">
                                {gear.isElectronic ? <Zap size={28} /> :
                                    gear.isRestraint ? <Lock size={28} /> :
                                        gear.isImpact ? <Target size={28} /> :
                                            gear.isWearable ? <Shirt size={28} /> :
                                                <Package size={28} />}
                            </div>

                            <div className="flex-1">
                                <h1 className="text-3xl font-fantasy font-semibold text-cream/95 mb-2 tracking-wide">
                                    {gear.name}
                                </h1>

                                <div className="flex flex-wrap items-center gap-3">
                                    {gear.category && (
                                        <span className="px-3 py-1 rounded-full bg-wine-950/50 text-[#C4A35A]/80 text-sm border border-wine-700/40">
                                            {gear.category}
                                        </span>
                                    )}
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${intensityClass}`}>
                                        Intensity: {gear.intensity || 5}/10
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Gold Ornate Divider */}
                        <div className="ornate-divider w-32 mb-4" />

                        {/* Flags */}
                        <div className="flex flex-wrap gap-2 mt-4">
                            {gear.isElectronic && (
                                <span className="px-3 py-1 text-sm rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                    ⚡ Electronic
                                </span>
                            )}
                            {gear.isRestraint && (
                                <span className="px-3 py-1 text-sm rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                    🔒 Restraint
                                </span>
                            )}
                            {gear.isImpact && (
                                <span className="px-3 py-1 text-sm rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">
                                    🎯 Impact
                                </span>
                            )}
                            {gear.isPenetrating && (
                                <span className="px-3 py-1 text-sm rounded-full bg-pink-500/10 text-pink-400 border border-pink-500/20">
                                    💫 Penetrating
                                </span>
                            )}
                            {gear.isWearable && (
                                <span className="px-3 py-1 text-sm rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                    👔 Wearable
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Description */}
                {gear.description && (
                    <section className="mb-8">
                        <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-3 tracking-wide">Description</h2>
                        <div className="prose prose-lg prose-invert text-cream/70 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{gear.description}</ReactMarkdown>
                        </div>
                    </section>
                )}

                {/* Usage Notes */}
                {gear.usageNotes && (
                    <section className="mb-8 p-5 rounded-xl bg-wine-950/30 border border-wine-800/30">
                        <div className="flex items-center gap-2 mb-3">
                            <Lightbulb className="w-5 h-5 text-[#C4A35A]" />
                            <h2 className="text-lg font-fantasy font-semibold text-cream/90 tracking-wide">Usage Notes</h2>
                        </div>
                        <p className="text-cream/70 leading-relaxed whitespace-pre-wrap">
                            {gear.usageNotes}
                        </p>
                    </section>
                )}

                {/* Safety Notes */}
                {gear.safetyNotes && (
                    <section className="mb-8 p-5 rounded-xl bg-amber-500/5 border border-amber-500/20">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 text-amber-400" />
                            <h2 className="text-lg font-semibold text-white">Safety Considerations</h2>
                        </div>
                        <p className="text-dark-300 leading-relaxed whitespace-pre-wrap">
                            {gear.safetyNotes}
                        </p>
                    </section>
                )}

                {/* Materials */}
                {gear.materialTypes && gear.materialTypes.length > 0 && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Materials</h2>
                        <div className="flex flex-wrap gap-2">
                            {gear.materialTypes.map((mat, idx) => (
                                <span key={idx} className="px-3 py-1 rounded-full bg-dark-800 text-dark-300 text-sm">
                                    {mat}
                                </span>
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Kinks */}
                {showRelatedKinks && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Related Kinks</h2>
                        <div className="space-y-2">
                            {gear.kinks.map(kink => (
                                <Link
                                    key={kink.id}
                                    to={`/kink/${kink.id}`}
                                    className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
                                >
                                    <div>
                                        <span className="text-white group-hover:text-wine-300 transition-colors">
                                            {kink.name}
                                        </span>
                                        {kink.category && (
                                            <span className="ml-2 text-sm text-dark-500">{kink.category}</span>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Sex Actions */}
                {showRelatedActions && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3">Related Actions</h2>
                        <div className="space-y-2">
                            {gear.sexActions.map(action => (
                                <Link
                                    key={action.id}
                                    to={`/action/${action.id}`}
                                    className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
                                >
                                    <div>
                                        <span className="text-white group-hover:text-wine-300 transition-colors">
                                            {action.name}
                                        </span>
                                        {action.category && (
                                            <span className="ml-2 text-sm text-dark-500">{action.category}</span>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Sub-Variants */}
                {gear.childVariants && gear.childVariants.length > 0 && (
                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                            <Package className="w-5 h-5 text-[#C4A35A]" />
                            Variants of {gear.name}
                        </h2>
                        <div className="space-y-2">
                            {gear.childVariants.map(variant => (
                                <Link
                                    key={variant.id}
                                    to={`/gear/${variant.id}`}
                                    className="flex items-center justify-between p-3 rounded-lg bg-dark-900/50 border border-dark-800 hover:border-wine-700/50 transition-colors group"
                                >
                                    <div>
                                        <span className="text-white group-hover:text-wine-300 transition-colors">
                                            {variant.name}
                                        </span>
                                        {variant.category && (
                                            <span className="ml-2 text-sm text-dark-500">{variant.category}</span>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-wine-400" />
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Dynamic Page Sections */}
                {gear.pageSections && gear.pageSections.length > 0 && (
                    <section className="mb-8">
                        <h2 className="text-xl font-fantasy font-semibold text-cream/90 mb-4 tracking-wide">More Details</h2>
                        <PageSections
                            pageSections={gear.pageSections}
                            prioritySections={['overview', 'size_guide', 'material_safety']}
                        />
                    </section>
                )}
            </div>
        </div>
    );
};

export default GearDetailPage;
